import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Cookies from "universal-cookie";
import queryString from "query-string";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { get, post } from "../services/api";
import {
  EmployeeErrorState,
  EmployeeLoadingState,
  EmployeePageHeader,
} from "../employee/components/EmployeePage";
import {
  AcknowledgementCard,
  calculateScore,
  CommentList,
  QuestionResult,
  ReportSection,
  Score,
  ScoreOverview,
} from "../employee/components/PerformanceReport";
import { tokens as t } from "../theme/tokens";

const ExpandIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const groupAnnualResults = (data: any[]) =>
  data.reduce((groups: Record<string, any[]>, period) => {
    (period.result || []).forEach((result: any) => {
      const reportPeriod = period.review_period || period.date || "Review period";
      const key = result.surveyquestiontypename === "Comment"
        ? `${reportPeriod}/${result.surveyquestionheadingname}_Comment`
        : `${result.surveyquestionheadingname}_${result.surveyquestiontypename}`;
      const item = { ...result, reportPeriod };
      groups[key] = groups[key] ? [...groups[key], item] : [item];
    });
    return groups;
  }, {});

const averageScore = (score: Score, periods: number): Score => ({
  result: Number((score.result / periods).toFixed(1)),
  total: Number((score.total / periods).toFixed(1)),
});

const EmployeeSurveyResultAnnual: React.FC = () => {
  const { employeeId, surveyId } = useParams();
  const parsedToken = queryString.parse(window.location.search).access_token;
  const publicToken = typeof parsedToken === "string" ? parsedToken : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [employeeDetails, setEmployeeDetails] = useState<any>({});
  const [reportPeriods, setReportPeriods] = useState<any[]>([]);
  const [reviewPeriod, setReviewPeriod] = useState<any[]>([]);
  const [generalComments, setGeneralComments] = useState("");
  const [surveyAck, setSurveyAck] = useState<{ date: string | null; comment: string }>({ date: null, comment: "" });

  const managerView = useMemo(() => {
    if (!publicToken) return false;
    try {
      return (jwtDecode(publicToken) as any).linkType === "viewSurveyResultsManager";
    } catch (_error) {
      return false;
    }
  }, [publicToken]);

  useEffect(() => {
    if (publicToken) localStorage.setItem("publicToken", publicToken);
    const cookieName = process.env.REACT_APP_PUBLIC_COOKIE_NAME || "";
    if (cookieName && publicToken) {
      new Cookies().set(cookieName, publicToken, { domain: ".paperlessnow.ca", path: "/surveyResultsAnnual" });
    }
  }, [publicToken]);

  useEffect(() => {
    if (!employeeId || !surveyId || !publicToken) {
      setError("This annual-report link is incomplete or has expired. Please contact your administrator for a fresh link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    get(`survey/getServices?type=getSurveyResultAckAnnual&surveyId=${surveyId}&access_token=${publicToken}`)
      .then((response: any) => {
        const acknowledgement = response?.payload?.data?.[0] || {};
        setSurveyAck({
          date: acknowledgement.ack_date_annual || null,
          comment: acknowledgement.ack_comments_annual || "No comments provided",
        });
      })
      .catch(() => undefined);

    Promise.all([
      get(`openEmployee/${employeeId}`, publicToken),
      get(`survey/getServices?type=getSurveyQuestionsAnnual&employeeId=${employeeId}&surveyId=${surveyId}&access_token=${publicToken}`),
    ])
      .then(([employeeResponse, reportResponse]: any[]) => {
        setEmployeeDetails(employeeResponse?.payload?.data || {});
        setReportPeriods(reportResponse?.payload?.data || []);
        setReviewPeriod(reportResponse?.payload?.reviewPeriod || []);
        setGeneralComments(reportResponse?.payload?.manager_comments_annual || "");
      })
      .catch(() => setError("We could not open this annual performance report. The link may have expired; please contact your administrator."))
      .finally(() => setLoading(false));
  }, [employeeId, publicToken, surveyId]);

  const groupedData = useMemo(() => groupAnnualResults(reportPeriods), [reportPeriods]);
  const detailGroups = Object.entries(groupedData).filter(([key]) => !key.endsWith("_Comment"));
  const scoredGroups = detailGroups.filter(([, items]) => calculateScore(items).result !== 0);
  const commentGroups = Object.entries(groupedData).filter(([key]) => key.endsWith("_Comment"));
  const flattenedResults = reportPeriods.flatMap((period) => period.result || []);
  const periods = Math.max(reportPeriods.length, 1);
  const overall = calculateScore(flattenedResults);
  const employeeName = [employeeDetails.first_name, employeeDetails.last_name].filter(Boolean).join(" ");

  if (loading) return <EmployeeLoadingState message="Loading your annual performance report…" />;
  if (error) return <EmployeeErrorState message={error} />;

  return (
    <Stack spacing={3}>
      <EmployeePageHeader
        eyebrow="Annual performance report"
        title={employeeName || "Employee performance report"}
        description="How your management team rated you across the last fiscal year."
        action={reviewPeriod[0]?.review_period_annual ? <Chip label={reviewPeriod[0].review_period_annual} color="primary" variant="outlined" /> : undefined}
      />

      <ScoreOverview
        overall={overall}
        overallDisplay={averageScore(overall, periods)}
        rows={scoredGroups.map(([key, items]) => {
          const score = calculateScore(items);
          return { key, label: key.split("_")[0], score, displayScore: averageScore(score, periods) };
        })}
      />

      <ReportSection title="Rating details" description="Expand a section to review ratings from each period.">
        <Stack spacing={1.5}>
          {detailGroups.map(([key, items]) => {
            const score = calculateScore(items);
            const display = averageScore(score, periods);
            return (
              <Accordion key={key} disableGutters sx={{ border: `1px solid ${t.color.cardLine}`, borderRadius: `${t.radius.control}px !important`, "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={ExpandIcon} aria-controls={`${key}-content`} id={`${key}-header`}>
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <Typography sx={{ fontWeight: 600 }}>{key.split("_")[0]}</Typography>
                    <Typography variant="body2" color="text.secondary">Average {display.result} of {display.total} points</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  {items.map((item: any, index: number) => (
                    <QuestionResult key={`${item.surveyquestionid}-${item.reportPeriod}-${index}`} item={item} period={item.reportPeriod} />
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </ReportSection>

      {(generalComments || commentGroups.length > 0) && (
        <ReportSection title="General comments" description={generalComments || "Written notes supplied with your annual ratings."}>
          <Stack spacing={1.5}>
            {commentGroups.map(([key, items]) => (
              <Accordion key={key} disableGutters sx={{ border: `1px solid ${t.color.cardLine}`, borderRadius: `${t.radius.control}px !important`, "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={ExpandIcon}>
                  <Typography sx={{ fontWeight: 600 }}>{key.split("_")[0].replace("/", " — ")}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <CommentList comments={items.map((item: any) => item.AnswerText)} />
                </AccordionDetails>
              </Accordion>
            ))}
            {!commentGroups.length && <CommentList comments={[]} />}
          </Stack>
        </ReportSection>
      )}

      <AcknowledgementCard
        date={surveyAck.date}
        comment={surveyAck.comment}
        managerView={managerView}
        onSubmit={(comments) =>
          post("survey/postServices", {
            type: "saveSurveyResultsAckAnnual",
            comments: comments || null,
            surveyId,
          })
        }
      />
    </Stack>
  );
};

export default EmployeeSurveyResultAnnual;
